/**
 * App.tsx
 *
 * Root component: providers + complete route tree for CBOps.
 *
 * Provider order (outer → inner):
 *   BrowserRouter → QueryClientProvider → ThemeProvider → AuthProvider → BillingProvider → ModuleProvider → PermissionsProvider
 *
 * Performance — code splitting:
 *   Every page component is loaded via React.lazy() so webpack emits a separate
 *   JS chunk per route.  The initial bundle only contains the auth page, the
 *   layout shell, and the provider tree.  All other pages are fetched on demand
 *   when the user first navigates to them.
 *
 *   A single <Suspense> boundary wraps the entire <Routes> tree.  The fallback
 *   is a centered spinner that matches the glassmorphism theme.
 *
 * Performance — React Query:
 *   <QueryClientProvider> is placed at the root so any component in the tree can
 *   call useQuery / useMutation.  The shared queryClient instance lives in
 *   src/lib/queryClient.ts.
 *
 * Route groups:
 *   /dashboard              — overview dashboard
 *   /performance/*          — KPIs, goals, check-ins, reviews, reports
 *   /people/*               — employees, org chart, recruitment
 *   /hr/*                   — leave management, attendance, documents, compliance
 *   /work/*                 — tasks, projects, time tracking
 *   /support/*              — my tickets, team queue, all tickets, SLA dashboard, knowledge base
 *   /communication/*        — channels, direct messages (all roles)
 *   /analytics/*            — overview + custom report builder
 *   /crm/*                  — dashboard, contacts, deals, activities
 *   /finance/*              — expenses, budgets, approvals, reports
 *   /owner/*                — super_admin owner dashboard + broadcast
 *   /admin/*                — holiday management, leave policies, users, company settings,
 *                             departments, billing, integrations, audit log, GDPR
 *   /settings               — user settings + privacy centre
 *
 * All routes except /login are wrapped in ProtectedRoute which redirects
 * unauthenticated users to /login and shows a full-screen spinner while
 * the Supabase session is being resolved.
 */

import React, { Suspense, lazy, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ModuleProvider, useModules } from './context/ModuleContext';
import { moduleForPath } from './lib/moduleRoutes';
import { isSignupEnabled } from './lib/signupEnabled';
import { PermissionsProvider } from './context/PermissionsContext';
import { BillingProvider } from './context/BillingContext';
import { NotificationProvider } from './context/NotificationContext';
import { CommandPaletteProvider } from './context/CommandPaletteContext';
import { PulseBotProvider } from './context/PulseBotContext';
import Spinner from './components/ui/Spinner';
import AwaitingProvisioning from './components/auth/AwaitingProvisioning';
import CompanySetupWizard from './components/admin/CompanySetupWizard';
import SetPasswordScreen from './pages/auth/SetPasswordScreen';
import { mustChangePassword } from './lib/authNative';

// ── Eagerly-loaded (always needed on first paint) ──────────────────────────────
import LoginPage       from './pages/auth/LoginPage';
import DashboardLayout from './components/layout/DashboardLayout';

// ── Multi-tenant sign-up (public pages) — gated by REACT_APP_SIGNUP_ENABLED ───
import { signupRoutes } from './pages/public/signup/SignupRoutes';

// ── Lazily-loaded pages (each becomes its own webpack chunk) ──────────────────

// Core
const DashboardPage     = lazy(() => import('./pages/dashboard/DashboardPage'));
const ProfilePage       = lazy(() => import('./pages/profile/ProfilePage'));

// Performance
const PerformanceDashboardPage = lazy(() => import('./pages/performance/PerformanceDashboardPage'));
const CompanyGoalsPage         = lazy(() => import('./pages/performance/CompanyGoalsPage'));
const KPITrackerPage         = lazy(() => import('./pages/performance/KPITrackerPage'));
const MyKPIsPage             = lazy(() => import('./pages/performance/MyKPIsPage'));
const WeeklyCheckinPage      = lazy(() => import('./pages/performance/WeeklyCheckinPage'));
const WeeklyReportsPage      = lazy(() => import('./pages/performance/WeeklyReportsPage'));
const PerformanceReviewsPage = lazy(() => import('./pages/performance/PerformanceReviewsPage'));

// People
const EmployeesPage   = lazy(() => import('./pages/people/EmployeesPage'));
const EmployeeProfilePage = lazy(() => import('./pages/people/EmployeeProfilePage'));
const OrgChartPage    = lazy(() => import('./pages/people/OrgChartPage'));
const RecruitmentPage = lazy(() => import('./pages/people/RecruitmentPage'));
const OnboardingPage  = lazy(() => import('./pages/people/OnboardingPage'));
const OffboardingPage = lazy(() => import('./pages/people/OffboardingPage'));
const TrainingPage    = lazy(() => import('./pages/people/TrainingPage'));

// HR
const MyLeavePage       = lazy(() => import('./pages/hr/MyLeavePage'));
const TeamLeavePage     = lazy(() => import('./pages/hr/TeamLeavePage'));
const LeaveCalendarPage = lazy(() => import('./pages/hr/LeaveCalendarPage'));
const AttendancePage    = lazy(() => import('./pages/hr/AttendancePage'));
const DocumentsPage     = lazy(() => import('./pages/hr/DocumentsPage'));
const CompliancePage    = lazy(() => import('./pages/hr/CompliancePage'));
const PromotionPage     = lazy(() => import('./pages/hr/PromotionPage'));
const ResignationPage   = lazy(() => import('./pages/hr/ResignationPage'));
const TerminationPage   = lazy(() => import('./pages/hr/TerminationPage'));
const HrReportsPage     = lazy(() => import('./pages/hr/ReportsPage'));
const MyHrPage          = lazy(() => import('./pages/hr/MyHrPage'));

// Company
const AnnouncementsPage = lazy(() => import('./pages/company/AnnouncementsPage'));
const AccessRequestsPage = lazy(() => import('./pages/company/AccessRequestsPage'));
const PoliciesPage      = lazy(() => import('./pages/company/PoliciesPage'));
const EventsPage        = lazy(() => import('./pages/company/EventsPage'));
const DirectoryPage     = lazy(() => import('./pages/company/DirectoryPage'));

// CRM
const CRMDashboardPage = lazy(() => import('./pages/crm/CRMDashboardPage'));
const LeadsPage        = lazy(() => import('./pages/crm/LeadsPage'));
const ContactsPage     = lazy(() => import('./pages/crm/ContactsPage'));
const CRMCompaniesPage = lazy(() => import('./pages/crm/CRMCompaniesPage'));
const DealsPage        = lazy(() => import('./pages/crm/DealsPage'));
const ProductsPage     = lazy(() => import('./pages/crm/ProductsPage'));
const ActivitiesPage   = lazy(() => import('./pages/crm/ActivitiesPage'));
const SalesReportsPage = lazy(() => import('./pages/crm/SalesReportsPage'));
const CRMSettingsPage  = lazy(() => import('./pages/crm/CRMSettingsPage'));

// Marketing
const MarketingDashboardPage = lazy(() => import('./pages/marketing/MarketingDashboardPage'));
const CampaignsPage          = lazy(() => import('./pages/marketing/CampaignsPage'));
const EmailMarketingPage     = lazy(() => import('./pages/marketing/EmailMarketingPage'));
const NurturingSequencesPage = lazy(() => import('./pages/marketing/NurturingSequencesPage'));
const LeadSourcesPage        = lazy(() => import('./pages/marketing/LeadSourcesPage'));

// Finance
const ExpensesPage         = lazy(() => import('./pages/finance/ExpensesPage'));
const InvoicesPage         = lazy(() => import('./pages/finance/InvoicesPage'));
const QuotesPage           = lazy(() => import('./pages/finance/QuotesPage'));
const PaymentsPage         = lazy(() => import('./pages/finance/PaymentsPage'));
const BudgetsPage          = lazy(() => import('./pages/finance/BudgetsPage'));
const ExpenseApprovalsPage = lazy(() => import('./pages/finance/ExpenseApprovalsPage'));
const FinanceReportsPage   = lazy(() => import('./pages/finance/FinanceReportsPage'));
const ARAgingPage          = lazy(() => import('./pages/finance/ARAgingPage'));

// Administration
const ProcurementPage = lazy(() => import('./pages/admin/ProcurementPage'));
const AssetsPage      = lazy(() => import('./pages/admin/AssetsPage'));
const VendorsPage     = lazy(() => import('./pages/admin/VendorsPage'));

// Owner
const OwnerDashboardPage = lazy(() => import('./pages/owner/OwnerDashboardPage'));
const BroadcastPage      = lazy(() => import('./pages/owner/BroadcastPage'));

// Billing / pricing
const BillingPage      = lazy(() => import('./pages/admin/BillingPage'));
const PricingPage      = lazy(() => import('./pages/PricingPage'));
const LandingPage      = lazy(() => import('./pages/LandingPage'));
const PublicFormPage   = lazy(() => import('./pages/public/PublicFormPage')); // public /f/:token (Phase 3 stub)
// Alongside it, and for the same reason: signed out, no workspace, no app shell.
const ForgotPasswordPage = lazy(() => import('./pages/public/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/public/ResetPasswordPage'));
const IntegrationsPage = lazy(() => import('./pages/admin/IntegrationsPage'));

// Admin
const HolidayManagementPage = lazy(() => import('./pages/admin/HolidayManagementPage'));
const LeavePoliciesPage     = lazy(() => import('./pages/admin/LeavePoliciesPage'));
const LeaveBalancesPage     = lazy(() => import('./pages/admin/LeaveBalancesPage'));
const UsersPage             = lazy(() => import('./pages/admin/UsersPage'));
const CompanySettingsPage   = lazy(() => import('./pages/admin/CompanySettingsPage'));
const DepartmentsTeamsPage  = lazy(() => import('./pages/admin/DepartmentsTeamsPage'));
const AuditLogPage          = lazy(() => import('./pages/admin/AuditLogPage'));
const GDPRRequestsPage      = lazy(() => import('./pages/admin/GDPRRequestsPage'));
const SetupChecklistPage    = lazy(() => import('./pages/admin/SetupChecklistPage'));
const AutomationPage        = lazy(() => import('./pages/admin/AutomationPage'));
const FormsListPage         = lazy(() => import('./pages/admin/FormsListPage'));
const FormBuilderPage       = lazy(() => import('./pages/admin/FormBuilderPage'));
const AccessPage            = lazy(() => import('./pages/admin/AccessScreen'));

// Work
const MyTasksPage       = lazy(() => import('./pages/work/MyTasksPage'));
const ProjectsPage      = lazy(() => import('./pages/work/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/work/ProjectDetailPage'));
const TimeTrackingPage  = lazy(() => import('./pages/work/TimeTrackingPage'));
const TimelinePage      = lazy(() => import('./pages/work/TimelinePage'));

// Support
const MyTicketsPage     = lazy(() => import('./pages/support/MyTicketsPage'));
const TeamQueuePage     = lazy(() => import('./pages/support/TeamQueuePage'));
const AllTicketsPage    = lazy(() => import('./pages/support/AllTicketsPage'));
const SLADashboardPage  = lazy(() => import('./pages/support/SLADashboardPage'));
const KnowledgeBasePage = lazy(() => import('./pages/support/KnowledgeBasePage'));
const MacrosPage        = lazy(() => import('./pages/support/MacrosPage'));

// Communication
const ChannelsPage       = lazy(() => import('./pages/communication/ChannelsPage'));
const DirectMessagesPage = lazy(() => import('./pages/communication/DirectMessagesPage'));

// Mail — top level on purpose, see the route below.
const MailPage           = lazy(() => import('./pages/mail/MailPage'));

// Analytics + other
const AnalyticsOverviewPage = lazy(() => import('./pages/analytics/AnalyticsOverviewPage'));
const CustomReportsPage     = lazy(() => import('./pages/analytics/CustomReportsPage'));
const SettingsPage          = lazy(() => import('./pages/SettingsPage'));
const NotificationsPage     = lazy(() => import('./pages/NotificationsPage'));
const DataPrivacyPage       = lazy(() => import('./pages/gdpr/DataPrivacyPage'));

// ── Suspense fallback ─────────────────────────────────────────────────────────

/**
 * Shown while a lazy page chunk is being downloaded.
 * Matches the glassmorphism gradient background so there is no flash of
 * unstyled content during the first navigation to a new route.
 */
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Spinner size="lg" />
    </div>
  );
}

// ── Route guards ──────────────────────────────────────────────────────────────

/**
 * Public root route (`/`).
 *
 * - While auth state is still resolving, shows the global page spinner.
 * - If the visitor is authenticated, redirects to `/dashboard` so they
 *   land where they expect (the app, not a marketing page).
 * - Otherwise, renders the public LandingPage with hero, modules grid,
 *   and CTAs into /login and /pricing.
 *
 * Keeping this as a separate small component (rather than inlining the
 * conditional in `<Routes>`) keeps the route tree readable.
 */
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

/**
 * Guards a route behind authentication.
 * Shows a full-screen spinner while auth state resolves.
 * Redirects to /login if no session, or to /dashboard if role doesn't match.
 */
function ProtectedRoute({
  children,
  roles,
}: {
  children: React.ReactNode;
  roles?: string[];
}) {
  const { user, profile, loading, signOut } = useAuth();
  const { canAccess, loading: modulesLoading } = useModules();
  const { pathname } = useLocation();
  const [mustChange, setMustChange] = useState(() => mustChangePassword());

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;

  /* A temporary password (2026-10-29): a person a super admin added, or whose password was
   * reset, chooses their own before any page opens. Held in state so the screen lifts itself
   * once the new password is saved. */
  if (mustChange) {
    return <SetPasswordScreen onDone={() => setMustChange(false)} onSignOut={() => void signOut()} />;
  }

  /* No workspace yet — hold, don't hand them an empty app.
   *
   * `handle_new_user` creates profiles with workspace_id NULL on purpose (0115), so
   * every workspace-scoped query would return nothing and the product would look
   * broken. Gated on `profile &&` so a still-loading or failed profile read falls
   * through to the existing behaviour rather than being misreported as unprovisioned —
   * "we could not read your profile" and "you have no workspace" are different states
   * and must not collapse into one screen. */
  if (profile && !profile.workspace_id) return <AwaitingProvisioning />;

  if (roles && profile && !roles.includes(profile.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  /* THE MODULE GATE — a hidden menu item is not a gate.
   *
   * Module state used to hide sidebar sections and command-palette actions and gate exactly
   * one page. Every other page was reachable by typing its URL, so a company that had not
   * bought Finance could still open /finance/invoices. This is the same shape of finding as
   * the role gates, and it gets the same answer: the ROUTER refuses.
   *
   * Which module owns which area lives in lib/moduleRoutes, as a prefix table rather than a
   * prop per route — a new page under /crm/ is then gated by existing, not by remembering.
   *
   * `canAccess` ANDs the company ceiling (billing + the super_admin's module switch) with
   * the signed-in person's own level from migration 0151 (`current_user_module_access`):
   * a module the company turned off, or that this person has no access to, refuses here.
   *
   * Waiting for `modulesLoading` matters: without it every deep link would bounce to the
   * dashboard for the first moment after sign-in, before the settings have arrived. */
  const requiredModule = moduleForPath(pathname);
  if (requiredModule) {
    if (modulesLoading) return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
    if (!canAccess(requiredModule)) return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Renders the CompanySetupWizard overlay when a super_admin has not yet
 * completed first-run setup. Once the wizard calls onComplete the flag is
 * set to true in the DB and the overlay disappears.
 */
function SetupWizardGuard({ children }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  const { company, loading, reload } = useModules();
  const [dismissed, setDismissed] = useState(false);

  const showWizard =
    !loading &&
    !dismissed &&
    profile?.role === 'super_admin' &&
    company !== null &&
    company.setup_complete === false;

  return (
    <>
      {children}
      {showWizard && (
        <CompanySetupWizard
          onComplete={async () => {
            setDismissed(true);
            await reload();
          }}
        />
      )}
    </>
  );
}

// ── Route tree ────────────────────────────────────────────────────────────────

/** Declares the complete route tree. All authenticated pages share DashboardLayout. */
function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/*
          * Public root: hands off to LandingPage for unauthenticated
          * visitors, redirects to /dashboard for authenticated ones.
          * Listed BEFORE the catch-all protected layout so React Router
          * matches "/" exactly here.
          */}
        <Route path="/"        element={<RootRoute />} />
        <Route path="/login"   element={<LoginPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        {/* Public form render (unauthenticated) — sits with the public routes, BEFORE the protected layout */}
        <Route path="/f/:token" element={<PublicFormPage />} />

        {/* ⛔ OUTSIDE THE SIGN-UP SWITCH, DELIBERATELY (Lis, 2026-11-02). Asking for a
            password reset is not part of signing up for a company, and it must work
            while sign-up is OFF - which is exactly when it is needed. Every OTHER
            sign-up route stays behind the switch below; these two are the exception,
            and `SignupRoutes.switch.test.tsx` fails if a third one joins them. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password"  element={<ResetPasswordPage />} />

        {/*
          Multi-tenant sign-up flow — public pages, no app-shell imports.
          ⛔ GATED by REACT_APP_SIGNUP_ENABLED (default OFF). When OFF none of
          these routes is registered (including /login/handoff), so every one
          falls through to the app's normal not-found behaviour (`path="*"`
          below). That is the whole point of the launch switch.
        */}
        {isSignupEnabled() && signupRoutes()}

        <Route
          element={
            <ProtectedRoute>
              <SetupWizardGuard>
                <DashboardLayout />
              </SetupWizardGuard>
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="profile"   element={<ProfilePage />} />

          {/* ── Performance ── */}
          <Route path="performance"          element={<Navigate to="/performance/dashboard" replace />} />
          <Route path="performance/dashboard" element={<PerformanceDashboardPage />} />
          <Route path="performance/goals"   element={<CompanyGoalsPage />} />
          <Route path="performance/kpis"    element={<KPITrackerPage />} />
          <Route path="performance/my-kpis" element={<MyKPIsPage />} />
          <Route path="performance/checkin" element={<WeeklyCheckinPage />} />
          {/*
            * Weekly KPI Reports was the one report page whose two gates DISAGREED. The
            * sidebar has always declared it as super_admin / dept_admin / hr_admin /
            * team_lead — so an ordinary employee never sees the link — but the route had no
            * guard, and a hidden nav item is not a gate: the URL, a bookmark, a shared link
            * or the command palette all bypass it. These roles are the app's own stated
            * intent, copied from Sidebar.tsx, not a new policy.
            *
            * It is a UI boundary, not a data one: `kpis_select` is `USING (true)`, so the
            * table itself is readable by any authenticated user. Real enforcement belongs
            * with the workspace-isolation sweep (docs/AUDIT_workspace_isolation_sweep.md),
            * where `kpis` is one of the named tables.
            */}
          <Route path="performance/reports"
            element={<ProtectedRoute roles={['super_admin','dept_admin','hr_admin','team_lead']}><WeeklyReportsPage /></ProtectedRoute>} />
          <Route path="performance/reviews" element={<PerformanceReviewsPage />} />

          {/* Legacy KPI routes — keep for backward compatibility */}
          <Route path="kpi/tracker"     element={<Navigate to="/performance/kpis" replace />} />
          <Route path="kpi/reports"     element={<Navigate to="/performance/reports" replace />} />
          <Route path="kpi/departments" element={<Navigate to="/performance/kpis" replace />} />

          {/* ── People ── */}
          <Route path="people/employees"   element={<EmployeesPage />} />
          <Route path="people/employees/:id" element={<EmployeeProfilePage />} />
          <Route path="people/org-chart"   element={<OrgChartPage />} />
          <Route path="people/recruitment" element={<RecruitmentPage />} />
          <Route path="people/onboarding"  element={<OnboardingPage />} />
          <Route path="people/offboarding" element={<OffboardingPage />} />
          <Route path="people/training"    element={<TrainingPage />} />

          {/* Legacy HR routes */}
          <Route path="hr/employees" element={<Navigate to="/people/employees" replace />} />

          {/* ── HR ── */}
          <Route path="hr/my-hr"      element={<MyHrPage />} />
          <Route path="hr/my-leave"   element={<MyLeavePage />} />
          <Route path="hr/leaves"     element={<Navigate to="/hr/my-leave" replace />} />
          <Route path="hr/team-leave" element={<TeamLeavePage />} />
          <Route path="hr/calendar"   element={<LeaveCalendarPage />} />
          <Route path="hr/attendance" element={<AttendancePage />} />
          <Route path="hr/documents"  element={<DocumentsPage />} />
          <Route path="hr/compliance" element={<CompliancePage />} />
          <Route path="hr/promotion"
            element={<ProtectedRoute roles={['super_admin','hr_admin']}><PromotionPage /></ProtectedRoute>} />
          {/*
            * Roadmap v3 §6: resignation is a self-service flow available to
            * every authenticated employee. Page itself shows different UI
            * based on role (employees see their own resignation only; HR
            * sees the full listing + ability to file on behalf of others).
            */}
          <Route path="hr/resignation" element={<ResignationPage />} />
          <Route path="hr/termination"
            element={<ProtectedRoute roles={['super_admin','hr_admin']}><TerminationPage /></ProtectedRoute>} />
          <Route path="hr/reports"
            element={<ProtectedRoute roles={['super_admin','hr_admin']}><HrReportsPage /></ProtectedRoute>} />

          {/* ── Company ── */}
          <Route path="company/announcements" element={<AnnouncementsPage />} />
          <Route path="company/policies"      element={<PoliciesPage />} />
          <Route path="company/events"        element={<EventsPage />} />
          <Route path="company/directory"     element={<DirectoryPage />} />
          {/* "Access requests" is part of the sign-up work — registered only
              when REACT_APP_SIGNUP_ENABLED is ON. */}
          {isSignupEnabled() && (
            <Route path="company/access-requests"
              element={<ProtectedRoute roles={['super_admin','admin','hr_admin','dept_admin']}><AccessRequestsPage /></ProtectedRoute>} />
          )}

          {/* ── Owner ── */}
          <Route
            path="owner/dashboard"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <OwnerDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="owner/broadcast"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <BroadcastPage />
              </ProtectedRoute>
            }
          />

          {/* ── Admin ── */}
          <Route
            path="admin/holidays"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin']}>
                <HolidayManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/leave-policies"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin']}>
                <LeavePoliciesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/leave-balances"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin']}>
                <LeaveBalancesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/users"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/access"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <AccessPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/company"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <CompanySettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/departments"
            element={
              <ProtectedRoute roles={['super_admin', 'dept_admin']}>
                <DepartmentsTeamsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/billing"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <BillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/integrations"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <IntegrationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/audit-log"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <AuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/gdpr"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <GDPRRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/setup"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <SetupChecklistPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/automation"
            element={
              <ProtectedRoute roles={['super_admin']}>
                <AutomationPage />
              </ProtectedRoute>
            }
          />

          {/* Legacy routes */}
          <Route path="users"       element={<Navigate to="/admin/users" replace />} />
          <Route path="hr/balances" element={<Navigate to="/admin/leave-balances" replace />} />

          {/* ── Work ── */}
          <Route path="work/my-tasks"      element={<MyTasksPage />} />
          <Route path="work/projects"      element={<ProjectsPage />} />
          <Route path="work/projects/:id"  element={<ProjectDetailPage />} />
          <Route path="work/timeline"      element={<TimelinePage />} />
          <Route path="work/time-tracking" element={<TimeTrackingPage />} />

          {/* ── CRM ── */}
          <Route path="crm/dashboard"  element={<CRMDashboardPage />} />
          <Route path="crm/leads"      element={<LeadsPage />} />
          <Route path="crm/contacts"   element={<ContactsPage />} />
          <Route path="crm/companies"  element={<CRMCompaniesPage />} />
          <Route path="crm/deals"      element={<DealsPage />} />
          <Route path="crm/products"   element={<ProductsPage />} />
          <Route path="crm/activities" element={<ActivitiesPage />} />
          <Route path="crm/reports"    element={<SalesReportsPage />} />
          <Route path="crm/settings"   element={<CRMSettingsPage />} />

          {/* ── Marketing ── */}
          <Route path="marketing/dashboard"   element={<MarketingDashboardPage />} />
          <Route path="marketing/campaigns"   element={<CampaignsPage />} />
          <Route path="marketing/email"       element={<EmailMarketingPage />} />
          <Route path="marketing/sequences"   element={<NurturingSequencesPage />} />
          <Route path="marketing/lead-sources" element={<LeadSourcesPage />} />

          {/* ── Finance ── */}
          <Route path="finance/expenses" element={<ExpensesPage />} />
          <Route path="finance/invoices"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin','finance_admin']}><InvoicesPage /></ProtectedRoute>} />
          <Route path="finance/quotes"
            element={<ProtectedRoute roles={['super_admin','hr_admin','finance_admin']}><QuotesPage /></ProtectedRoute>} />
          <Route path="finance/payments"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin','finance_admin']}><PaymentsPage /></ProtectedRoute>} />
          <Route path="finance/ar-aging"
            element={<ProtectedRoute roles={['super_admin','hr_admin','finance_admin']}><ARAgingPage /></ProtectedRoute>} />
          <Route
            path="finance/budgets"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin', 'dept_admin']}>
                <BudgetsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/approvals"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin', 'dept_admin', 'team_lead']}>
                <ExpenseApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="finance/reports"
            element={
              <ProtectedRoute roles={['super_admin', 'hr_admin']}>
                <FinanceReportsPage />
              </ProtectedRoute>
            }
          />

          {/* ── Administration ── */}
          <Route path="admin/procurement"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin']}><ProcurementPage /></ProtectedRoute>} />
          <Route path="admin/assets"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin']}><AssetsPage /></ProtectedRoute>} />
          <Route path="admin/vendors"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin']}><VendorsPage /></ProtectedRoute>} />
          {/* Forms engine builder (Phase 2) — create via modal on the list page → /admin/forms/:id (no /new route, matches app convention) */}
          <Route path="admin/forms"
            element={<ProtectedRoute roles={['super_admin','dept_admin']}><FormsListPage /></ProtectedRoute>} />
          <Route path="admin/forms/:id"
            element={<ProtectedRoute roles={['super_admin','dept_admin']}><FormBuilderPage /></ProtectedRoute>} />

          {/* ── Support Desk ── */}
          <Route path="support/my-tickets"     element={<MyTicketsPage />} />
          <Route path="support/team-queue"     element={<TeamQueuePage />} />
          <Route path="support/all-tickets"    element={<AllTicketsPage />} />
          <Route path="support/sla-dashboard"  element={<SLADashboardPage />} />
          <Route path="support/knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="support/macros"
            element={<ProtectedRoute roles={['super_admin','hr_admin','dept_admin','team_lead']}><MacrosPage /></ProtectedRoute>} />

          {/* ── Communication ── */}
          <Route path="communication/channels"        element={<ChannelsPage />} />
          <Route path="communication/direct-messages" element={<DirectMessagesPage />} />

          {/* ── Mail ──
              ⛔ `/mail`, NOT `/communication/mail`, on Lis's ruling.

              `/communication/mail` does not "redirect" by design — it matches no route and
              falls through the `path="*"` catch-all below to `/dashboard`. Untangling that
              is not this piece's work.

              ⚠️ AND IT IS DELIBERATELY **NOT A ModuleKey**. A real ModuleKey fans out to ten
              frontend files plus a migration touching three live CHECK constraints. This is
              the URL-prefix approach `/company/*` already uses — and verified 2026-09-02,
              App.tsx has NO module gating of any kind, so there is nothing to opt into.
              One line is genuinely the whole route. */}
          <Route path="mail" element={<MailPage />} />

          {/* ── Analytics + other ── */}
          <Route path="analytics/overview" element={<AnalyticsOverviewPage />} />
          <Route path="analytics/reports"  element={<CustomReportsPage />} />
          <Route path="settings"           element={<SettingsPage />} />
          <Route path="settings/privacy"   element={<DataPrivacyPage />} />
          <Route path="notifications"      element={<NotificationsPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────

/** Application entry point — composes providers around the complete route tree. */
export default function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <NotificationProvider>
              <CommandPaletteProvider>
                <PulseBotProvider>
                  {/* BillingProvider is now OUTSIDE ModuleProvider, and the order is
                      load-bearing: `isEnabled()` ANDs the admin's module switch with the
                      ceiling of what the company actually bought, so ModuleContext has to
                      be able to read billing. Doing it there rather than at each gate is
                      what gives the sidebar, the command palette, RequirePermission and
                      the router the same answer without four copies of the rule.
                      BillingProvider itself reads nothing from ModuleContext. */}
                  <BillingProvider>
                    <ModuleProvider>
                      <PermissionsProvider>
                        <AppRoutes />
                      </PermissionsProvider>
                    </ModuleProvider>
                  </BillingProvider>
                </PulseBotProvider>
              </CommandPaletteProvider>
            </NotificationProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
}
